import { FC, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import {
	HardFilterWrapper,
	HardFilterWrapperHandle,
	UfinetActionButtonHandle,
	UfinetBox,
	UfinetSectionBox,
} from 'ufinet-web-components'
import {
	areaService,
	AuthContext,
	defaultPageNumber,
	defaultPageSize,
	EmptyPagingData,
	IAreaResponse,
	INetworkAreaRequest,
	mapFromPagedResponseToPagingData,
	PagedResponse,
	PagingData,
	useAsync,
} from 'ufinet-web-functions'
import { DataTablePFSEvent } from 'primereact/datatable'
import {
	buildEmptyPagedResponse,
	getCountryIdsFromTableFilter,
	mergeCountryFilters,
	useCostsCountryCatalog,
} from 'pages/costs/common/useCostsCountryFilters'
import { useTableSort } from 'pages/costs/common/useTableSort'
import { AreasTable } from './table/AreasTable'

const AreasPage: FC = () => {
	const intl = useIntl()
	const actionButtonRef = useRef<UfinetActionButtonHandle>()
	const [areas, setAreas] = useState<IAreaResponse[]>([])
	const { handleSort, sortData } = useTableSort<IAreaResponse>()
	const [pagingData, setPagingData] = useState<PagingData>(EmptyPagingData)
	const [pagingState, setPagingState] = useState({ pageNumber: defaultPageNumber, pageSize: defaultPageSize })
	const [tableCountryFilterIds, setTableCountryFilterIds] = useState<string[]>([])

	const [loadingRecords, setLoadingRecords] = useState<boolean>(true)

	const authData = useContext(AuthContext)
	const { countryFilterOptions } = useCostsCountryCatalog(authData)
	const _areaService = useMemo(() => areaService(authData, intl), [authData, intl])

	const upperFilterRef = useRef<HardFilterWrapperHandle>(null)

	const getAreas = useCallback(async () => {
		setLoadingRecords(true)
		actionButtonRef.current?.changeActionStatus(true)
		const hardFilterCountryIds = upperFilterRef.current?.getCountries().map((e) => e.value) || []
		const { countries, hasImpossibleCountryFilter } = mergeCountryFilters(hardFilterCountryIds, tableCountryFilterIds)
		if (hasImpossibleCountryFilter) {
			return buildEmptyPagedResponse<IAreaResponse>(pagingState.pageNumber, pagingState.pageSize)
		}

		const criteria = {
			countries,
			networkAreaTypes: upperFilterRef.current?.getAreaTypes().map((e) => e.value) || [],
			networkAreas: [],
			pageNumber: pagingState.pageNumber,
			pageSize: pagingState.pageSize,
		} as INetworkAreaRequest & { pageNumber: number; pageSize: number }
		return await _areaService.findArea(criteria)
	}, [_areaService, pagingState.pageNumber, pagingState.pageSize, tableCountryFilterIds])

	const onAreasFetched = (response: PagedResponse<IAreaResponse> | IAreaResponse[]): void => {
		const isLegacy = Array.isArray(response)
		const data = isLegacy ? (response as IAreaResponse[]) : (response as PagedResponse<IAreaResponse>).data

		const parsedAreas = data.map((area) => ({
			...area,
			typeName: intl.formatMessage({ id: `AREA_TYPE.${area.typeId}` }),
		}))

		if (!isLegacy) {
			setPagingData(mapFromPagedResponseToPagingData(response as PagedResponse<IAreaResponse>))
		}
		setAreas(parsedAreas)
	}

	const onAreasFailedToFetch = (): void => {
		toast.error(intl.formatMessage({ id: 'AREAS.FETCH.ERROR' }))
	}

	const runFinally = () => {
		setLoadingRecords(false)
		actionButtonRef.current?.changeActionStatus(false)
	}

	const fetchAndStore = useAsync(
		{
			asyncFn: getAreas,
			onSuccess: onAreasFetched,
			onFailure: onAreasFailedToFetch,
			runFinally: runFinally,
		},
		[pagingState.pageNumber, pagingState.pageSize, tableCountryFilterIds]
	)

	const handlePage = (event: DataTablePFSEvent) => {
		setPagingState({ pageNumber: event.page ?? defaultPageNumber, pageSize: event.rows ?? defaultPageSize })
	}
	const handleFilter = (event: DataTablePFSEvent) => {
		const countryIds = getCountryIdsFromTableFilter(event)
		setPagingState((prev) => ({ ...prev, pageNumber: defaultPageNumber }))
		setTableCountryFilterIds(countryIds)
	}

	return (
		<UfinetBox>
			<HardFilterWrapper
				ref={upperFilterRef}
				actionButtonRef={actionButtonRef}
				search={fetchAndStore}
				hidden={{ hideAreaTypes: false, hideAreas: true }}
			/>
			<UfinetSectionBox>
				<AreasTable
					searchAreas={fetchAndStore}
					areas={sortData(areas)}
					loadingRecords={loadingRecords}
					pagingData={pagingData}
					countryFilterOptions={countryFilterOptions}
					onPage={handlePage}
					onSort={handleSort}
					onFilter={handleFilter}
				/>
			</UfinetSectionBox>
		</UfinetBox>
	)
}

export { AreasPage }
